/*
 * InvisiBra size guide UI: region tabs on the chart, and the measuring stepper.
 * Sizing logic and field checks live in invisibra-sizing.js (window.InvisiSizing); all customer-facing
 * copy lives in the sections' <template> elements, so it's edited in the theme editor, not here.
 */
(() => {
  const S = window.InvisiSizing;
  if (!S) return;

  const findData = (el) => S.fromPage(el) || S.fromPage(document);

  if (!customElements.get('invisibra-size-chart')) {
    class InvisibraSizeChart extends HTMLElement {
      connectedCallback() {
        this.data = findData(this);
        const regions = this.querySelector('[data-regions]');
        if (!this.data || !regions) return;
        regions.hidden = false;
        regions.addEventListener('change', (event) => {
          if (event.target.matches('input[type="radio"]')) this.relabel(event.target.value);
        });
      }

      relabel(region) {
        const r = S.REGIONS[region] || S.REGIONS.UK;
        this.querySelectorAll('th[data-cup-index]').forEach((th) => {
          th.textContent = r.cups[Number(th.dataset.cupIndex)];
        });
        this.querySelectorAll('th[data-band]').forEach((th) => {
          th.textContent = r.band(Number(th.dataset.band));
        });
      }

      highlight(uk) {
        this.querySelectorAll('.is-match').forEach((cell) => cell.classList.remove('is-match'));
        if (!uk) return;
        const cell = this.querySelector(`[data-uk="${CSS.escape(uk)}"]`);
        if (cell) cell.classList.add('is-match');
      }
    }
    customElements.define('invisibra-size-chart', InvisibraSizeChart);
  }

  if (!customElements.get('invisibra-size-calculator')) {
    const FIELDS = ['underbust', 'standing', 'leaning', 'lying'];
    const round1 = (x) => Math.round(x * 10) / 10;
    const highlightChart = (uk) => {
      const chart = document.querySelector('invisibra-size-chart');
      if (chart && typeof chart.highlight === 'function') chart.highlight(uk || null);
    };

    /*
     * One measurement per step: image, instruction, box, helper and error text, Back/Next.
     * The tracker shows where you are and the values so far; finished steps can be revisited.
     * Without JavaScript all four steps simply show at once.
     */
    class InvisibraSizeCalculator extends HTMLElement {
      connectedCallback() {
        this.data = findData(this);
        this.form = this.querySelector('form');
        this.output = this.querySelector('[data-result]');
        if (!this.data || !this.form || !this.output) return;

        this.steps = FIELDS.map((name) => this.querySelector(`[data-step="${name}"]`));
        this.tracker = this.querySelector('[data-tracker]');
        this.tracks = [...this.querySelectorAll('[data-track]')];
        this.progress = this.querySelector('[data-progress]');
        this.resultNav = this.querySelector('[data-result-nav]');
        this.templates = {};
        this.querySelectorAll('template[data-status]').forEach((t) => {
          this.templates[t.dataset.status] = t;
        });
        this.messages = {};
        this.querySelectorAll('template[data-msg]').forEach((t) => {
          this.messages[t.dataset.msg] = t.content.textContent.trim();
        });

        const checked = this.querySelector('input[name="unit"]:checked');
        this.unit = checked && checked.value === 'cm' ? 'cm' : 'in';
        this.current = 0;
        this.reached = 0;
        this.finished = false;
        this.result = null;

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.next();
        });
        this.form.addEventListener('input', (event) => {
          if (!FIELDS.includes(event.target.name)) return;
          this.clearError(event.target.name);
          if (this.finished) {
            this.finished = false;
            this.updateTracker();
          }
        });
        this.querySelectorAll('input[name="unit"]').forEach((radio) => {
          radio.addEventListener('change', () => this.setUnit(radio.value));
        });
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-back]')) return this.show(this.current - 1, true);
          if (event.target.closest('[data-edit]')) return this.show(FIELDS.length - 1, true);
          if (event.target.closest('[data-restart]')) return this.restart();
          const go = event.target.closest('[data-go]');
          if (go && !go.disabled) this.show(Number(go.dataset.go), true);
        });

        this.classList.add('is-stepper');
        this.tracker.hidden = false;
        this.renderUnits();
        this.show(0, false);
      }

      field(name) {
        return this.form.elements[name];
      }

      label(name) {
        const el = this.steps[FIELDS.indexOf(name)].querySelector('[data-step-label]');
        return el ? el.textContent.trim() : name;
      }

      unitShort() {
        return this.unit === 'cm' ? 'cm' : 'in';
      }

      text(key, tokens) {
        let text = this.messages[key] || '';
        Object.keys(tokens).forEach((token) => {
          text = text.split(token).join(tokens[token]);
        });
        return text;
      }

      tokens(name) {
        const [min, max] = S.fieldRange(name, this.unit);
        const underbust = S.parseMeasurement(this.field('underbust').value);
        return {
          '[min]': String(min),
          '[max]': String(max),
          '[unit]': this.unit === 'cm' ? 'cm' : 'inches',
          '[field]': this.label(name).toLowerCase(),
          '[underbust]': underbust === null || Number.isNaN(underbust) ? '' : String(underbust),
        };
      }

      check(name) {
        return S.checkField(name, this.field(name).value, this.unit, this.field('underbust').value);
      }

      renderUnits() {
        FIELDS.forEach((name, i) => {
          this.steps[i].querySelector('[data-helper]').textContent = this.text('helper', this.tokens(name));
          this.steps[i].querySelector('[data-unit-suffix]').textContent = this.unitShort();
        });
      }

      showError(name, code) {
        const el = this.steps[FIELDS.indexOf(name)].querySelector('[data-error]');
        el.textContent = this.text(`error-${code}`, this.tokens(name));
        el.hidden = false;
        this.field(name).setAttribute('aria-invalid', 'true');
      }

      clearError(name) {
        const el = this.steps[FIELDS.indexOf(name)].querySelector('[data-error]');
        el.hidden = true;
        el.textContent = '';
        this.field(name).removeAttribute('aria-invalid');
      }

      next() {
        if (this.current >= FIELDS.length) return;
        const name = FIELDS[this.current];
        const code = this.check(name);
        if (code) {
          this.showError(name, code);
          this.field(name).focus();
          return;
        }
        this.clearError(name);
        if (this.current < FIELDS.length - 1) {
          this.show(this.current + 1, true);
          return;
        }
        // An earlier answer may have been edited into conflict (say, a bigger underbust): go back to it.
        const bad = FIELDS.find((f) => this.check(f));
        if (bad) {
          this.show(FIELDS.indexOf(bad), true);
          this.showError(bad, this.check(bad));
          return;
        }
        this.finished = true;
        this.renderResult();
        this.show(FIELDS.length, true);
      }

      show(index, focus) {
        if (index < 0) return;
        this.current = index;
        this.reached = Math.max(this.reached, Math.min(index, FIELDS.length - 1));
        const onResult = index >= FIELDS.length;
        this.steps.forEach((step, i) => {
          step.hidden = i !== index;
        });
        // A saved answer can conflict after an earlier edit (a bigger underbust): say so straight away.
        if (!onResult && this.field(FIELDS[index]).value.trim() !== '') {
          const code = this.check(FIELDS[index]);
          if (code) this.showError(FIELDS[index], code);
        }
        this.form.hidden = onResult;
        this.output.hidden = !onResult;
        this.resultNav.hidden = !onResult;
        this.updateTracker();
        this.progress.textContent = onResult
          ? this.text('progress-done', {})
          : this.text('progress', { '[n]': String(index + 1), '[total]': String(FIELDS.length), '[field]': this.label(FIELDS[index]) });

        if (!focus) return;
        if (this.tracker.getBoundingClientRect().top < 0) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.tracker.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
        }
        (onResult ? this.output : this.field(FIELDS[index])).focus({ preventScroll: true });
      }

      updateTracker() {
        this.tracks.forEach((li, i) => {
          const isResult = i === FIELDS.length;
          let state = 'todo';
          if (i === this.current) state = 'current';
          else if (isResult ? this.finished : i <= this.reached && !this.check(FIELDS[i])) state = 'done';

          li.dataset.state = state;
          if (state === 'current') li.setAttribute('aria-current', 'step');
          else li.removeAttribute('aria-current');
          li.querySelector('[data-go]').disabled = state !== 'done';

          const value = li.querySelector('[data-track-value]');
          if (isResult) {
            value.textContent = this.finished && this.result && this.result.sizeLabel ? this.result.sizeLabel.replace(/^InvisiBra /, '') : '';
          } else {
            value.textContent = state === 'done' ? `${S.parseMeasurement(this.field(FIELDS[i]).value)} ${this.unitShort()}` : '';
          }
        });
      }

      fill(status, result) {
        const template = this.templates[status];
        if (!template) return null;
        const between = result.between || {};
        const tokens = {
          '[uk]': result.uk || '',
          '[size]': result.sizeLabel || '',
          '[band]': result.band ? String(result.band) : '',
          '[unit]': this.unit === 'cm' ? 'cm' : 'inches',
          '[lower]': between.lowerLabel || '',
          '[upper]': between.upperLabel || '',
          '[product]': between.productLabel || '',
        };
        const fragment = template.content.cloneNode(true);
        const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) {
          let text = walker.currentNode.nodeValue;
          Object.keys(tokens).forEach((token) => {
            text = text.split(token).join(tokens[token]);
          });
          walker.currentNode.nodeValue = text;
        }
        return fragment;
      }

      renderResult() {
        const input = { unit: this.unit };
        FIELDS.forEach((name) => {
          input[name] = S.parseMeasurement(this.field(name).value);
        });
        const result = S.fromMeasurements(this.data, input);
        this.result = result;
        const parts = [this.fill(result.status, result)];
        if (result.status === 'ok' && result.between) parts.push(this.fill('between', result));
        if (result.status === 'ok' && result.between && result.between.product) parts.push(this.fill('between-product', result));
        if (result.status === 'ok' && result.aboveLimit) parts.push(this.fill('above-limit', result));
        this.output.replaceChildren(...parts.filter(Boolean));
        this.output.dataset.status = result.status;
        highlightChart(result.uk);
      }

      restart() {
        FIELDS.forEach((name) => {
          this.field(name).value = '';
          this.clearError(name);
        });
        this.finished = false;
        this.result = null;
        this.reached = 0;
        this.output.replaceChildren();
        highlightChart(null);
        this.show(0, true);
      }

      setUnit(unit) {
        const next = unit === 'cm' ? 'cm' : 'in';
        if (next === this.unit) return;
        const factor = next === 'cm' ? S.CM_PER_INCH : 1 / S.CM_PER_INCH;
        FIELDS.forEach((name) => {
          const n = S.parseMeasurement(this.field(name).value);
          if (n !== null && !Number.isNaN(n)) this.field(name).value = String(round1(n * factor));
          this.clearError(name);
        });
        this.unit = next;
        this.renderUnits();
        if (this.finished) this.renderResult();
        this.updateTracker();
      }
    }
    customElements.define('invisibra-size-calculator', InvisibraSizeCalculator);
  }
})();